import _defaultTo from 'lodash/defaultTo';
import _find from 'lodash/find';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import _toFinite from 'lodash/toFinite';
// Serialization
import { deserialize, serialize } from 'serializr';
import {
  customerAccessWaySchema,
  customerDailyCodesSchema,
  customerSchema,
} from './schemas';
// Color
import { occupancyStatePalette } from '../components/core/colors';
// Custom
import { SEARCHFIELD_TYPES } from '../constants/App';
import { joinNonEmpty, formatPhoneNumberSubmit, findInList } from '../utils/Tools';

/**
* Serialization
*/
const serializerKey = 'customer';

const deserializeCustomerAccessWay = (data) => deserialize(customerAccessWaySchema, data);

const serializeCustomerAccessWay = ({
  type, desc, enabled, rights,
}) => {
  // Build desc according to type
  let finalDesc;
  if (!_isNil(desc)) {
    finalDesc = type === 'phone' ? formatPhoneNumberSubmit(desc) : desc;
  }
  return {
    ...serialize(customerAccessWaySchema, {
      type: _defaultTo(type, null),
      ...(!_isNil(enabled) && { enabled }),
      desc: _defaultTo(finalDesc, null),
      ...{ rights: rights || [] },
    }),
  };
};

const deserializeCustomer = (data) => deserialize(customerSchema, data);

const serializeCustomer = ({
  entityId,
  firstname,
  lastname,
  email,
  comments,
  metadata,
  accessWays,
  weight,
  restrictedByAntipassbackEntrance,
  restrictedByAntipassbackExit,
  stateInsideQuantity,
  stateOutsideQuantity,
}) => ({
  [serializerKey]: {
    ...serialize(customerSchema, {
      entity_id: _defaultTo(entityId, null),
      ...(firstname && { firstname }),
      lastname: _defaultTo(lastname, null),
      ...(email && { email }),
      ...(comments && { comments }),
      ...(accessWays && { access_ways: accessWays }),
      ...(metadata && { metadata }),
      weight: _defaultTo(weight, null),
      ...(!_isNil(restrictedByAntipassbackEntrance) && { restricted_by_antipassback_entrance: restrictedByAntipassbackEntrance }),
      ...(!_isNil(restrictedByAntipassbackExit) && { restricted_by_antipassback_exit: restrictedByAntipassbackExit }),
      ...((!_isNil(stateInsideQuantity) || !_isNil(stateOutsideQuantity)) && {
        state: {
          inside_quantity: _defaultTo(stateInsideQuantity, 0),
          outside_quantity: _defaultTo(stateOutsideQuantity, 0),
        },
      }),
    }),
  },
});

const serializeCustomerState = ({
  customer, insideQuantity, outsideQuantity,
}) => ({
  [serializerKey]: {
    ...serialize(customerSchema, {
      ...customer,
      state: {
        inside_quantity: _defaultTo(insideQuantity, 0),
        outside_quantity: _defaultTo(outsideQuantity, 0),
      },
    }),
  },
});

const serializeCustomerDailyCodes = ({
  entityId, begin, end, prefixLastname,
}) => ({
  ...serialize(customerDailyCodesSchema, {
    entity_id: _defaultTo(entityId, null),
    begin: _defaultTo(begin, null),
    end: _defaultTo(end, null),
    prefix_lastname: _defaultTo(prefixLastname, null),
  }),
});

/**
* Lists
*/
const customerOpBookingUsages = [
  {
    key: 'b2b_subscription',
    value: 'b2b_subscription',
    labelId: 'customer.opBookingUsage.b2bSubscription',
    icon: 'customerOpBookingUsageB2bSubscription',
  },
  {
    key: 'package',
    value: 'package',
    labelId: 'customer.opBookingUsage.package',
    icon: 'customerOpBookingUsagePackage',
  },
  {
    key: 'subscription',
    value: 'subscription',
    labelId: 'customer.opBookingUsage.subscription',
    icon: 'customerOpBookingUsageSubscription',
  },
];

/**
* Getters
*/
const customerGetOpBookingUsageB2b = customerOpBookingUsages[0];

/**
* Utils
*/
const customerFullName = (customer) => (customer ? joinNonEmpty([customer.firstname, customer.lastname], ' ') : '');
const customerTitle = (customerData) => customerFullName(customerData);
const customerStateDesc = (value, weight) => {
  if (value > weight) {
    return 'over';
  } if (value === weight) {
    return 'all';
  } if (value > 0) {
    return 'some';
  }
  return 'none';
};

const customerStatesInfo = (customer) => {
  const {
    weight, state: {
      inside_quantity: insideQuantity,
      outside_quantity: outsideQuantity,
      unknown_quantity: unknownQuantity,
    },
  } = customer;

  const insideQuantityDesc = customerStateDesc(insideQuantity, weight);
  const outsideQuantityDesc = customerStateDesc(outsideQuantity, weight);
  const unknownQuantityDesc = customerStateDesc(unknownQuantity, weight);

  return {
    inside: {
      key: 'insideQuantity',
      value: insideQuantity,
      iconId: 'occupancyStateInside',
      labelId: 'vehicles.state.insideQuantity',
      desc: insideQuantityDesc,
      color: occupancyStatePalette[insideQuantityDesc],
    },
    outside: {
      key: 'outsideQuantity',
      value: outsideQuantity,
      iconId: 'occupancyStateOutside',
      labelId: 'vehicles.state.outsideQuantity',
      desc: outsideQuantityDesc,
      color: occupancyStatePalette[outsideQuantityDesc],
    },
    unknown: {
      key: 'unknownQuantity',
      value: unknownQuantity,
      iconId: 'occupancyStateUnknown',
      labelId: 'vehicles.state.unknownQuantity',
      desc: unknownQuantityDesc,
      color: occupancyStatePalette[unknownQuantityDesc],
    },
  };
};

const customerOpBookingUsage = (customer) => findInList(customerOpBookingUsages, 'value', customer.metadata, 'booking_usage');
const customerIsOpBookingUsageB2b = (customer) => customerOpBookingUsage(customer) === customerGetOpBookingUsageB2b;
const customerGetAccessKeypadCode = (customer) => _get(_find(customer.access_ways, ['type', 'keypad_code']), 'desc', null);
const dummyCustomer = (id) => ({ id: _toFinite(id), lastname: `dummy_${id}` });

/**
* Model
*/
const customer = {
  // Name
  name: 'customer',

  // Serialization
  deserialize: deserializeCustomer,
  serialize: serializeCustomer,
  serializeState: serializeCustomerState,
  deserializeAccessWay: deserializeCustomerAccessWay,
  serializeAccessWay: serializeCustomerAccessWay,
  serializeDailyCodes: serializeCustomerDailyCodes,

  // Utils
  title: customerTitle,
  fullName: customerFullName,
  dummy: dummyCustomer,
  statesInfo: customerStatesInfo,
  opBookingUsage: customerOpBookingUsage,
  isOpBookingUsageB2b: customerIsOpBookingUsageB2b,
  getAccessKeypadCode: customerGetAccessKeypadCode,

  // List features
  listFeatures: ({ antipassbackEnabled, withEntity, withOneParkBooking } = {}) => ({
    // Default list params
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: [
        { field: 'lastname', dir: 'asc' },
        { field: 'firstname', dir: 'asc' },
      ],
    },

    // Autocomplete filter
    autoCompleteFilter: {
      field: 'lastname',
      operator: SEARCHFIELD_TYPES.string.defaultOperator(),
    },

    // Columns
    columnsQuantity: () => ({
      xs: 2,
      sm: 2,
      md: 3 + (withEntity | 0),
      lg: 4 + (withEntity | 0) + (withOneParkBooking | 0),
      xl: 7 + (withEntity | 0) + (antipassbackEnabled | 0) + (withOneParkBooking | 0),
    }),
    columns: [
      {
        key: 'lastname',
        labelId: 'field.lastname',
        field: 'lastname',
        sortable: true,
      },
      {
        key: 'firstname',
        labelId: 'field.firstname',
        field: 'firstname',
        sortable: true,
        secondarySorts: [
          { field: 'lastname', dir: 'asc' },
        ],
        className: 'hidden-sm-down',
      },
      {
        key: 'email',
        labelId: 'field.email',
        field: 'email',
        sortable: true,
        secondarySorts: [
          { field: 'lastname', dir: 'asc' },
          { field: 'firstname', dir: 'asc' },
        ],
        className: 'hidden-lg-down',
      },
      {
        key: 'comments',
        labelId: 'field.comments',
        field: 'comments',
        sortable: true,
        secondarySorts: [
          { field: 'lastname', dir: 'asc' },
          { field: 'firstname', dir: 'asc' },
        ],
        className: 'hidden-lg-down',
      },
      {
        key: 'entity',
        hidden: !withEntity,
        labelId: 'field.entity',
        field: 'entity',
        sortable: false,
        className: 'hidden-sm-down',
      },
      {
        key: 'opInfo',
        hidden: !withOneParkBooking,
        labelId: 'field.opInfo',
        field: 'opInfo',
        sortable: false,
        className: 'hidden-md-down',
      },
      {
        key: 'lastAccess',
        labelId: 'field.lastAccess',
        field: 'last_installation_log.date',
        alias: 'last_access',
        sortable: true,
        secondarySorts: [
          { field: 'lastname', dir: 'asc' },
        ],
        className: 'hidden-lg-down',
      },
      {
        key: 'state',
        labelId: 'field.state',
        field: 'state',
        sortable: false,
        className: 'table-content-1 hidden-md-down',
      },
      {
        hidden: !antipassbackEnabled,
        key: 'antipassback',
        labelId: 'field.antipassback',
        field: 'antipassback',
        sortable: false,
        className: 'table-content-2 hidden-lg-down',
      },
      {
        key: 'status',
        labelId: 'field.status',
        field: 'status',
        sortable: false,
        className: 'table-content-1 table-content-center',
      },
    ],

    // Search fields
    searchFields: [
      {
        key: 'lastname',
        labelId: 'field.lastname',
        field: 'lastname',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        filter: {
          field: 'lastname',
        },
      },
      {
        key: 'firstname',
        labelId: 'field.firstname',
        field: 'firstname',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        filter: {
          field: 'firstname',
        },
      },
      {
        key: 'email',
        labelId: 'field.email',
        field: 'email',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        filter: {
          field: 'email',
        },
      },
      {
        key: 'comments',
        labelId: 'field.comments',
        field: 'comments',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        filter: {
          field: 'comments',
        },
      },
      {
        key: 'accessWay',
        labelId: 'field.accessWay',
        field: 'accessWay',
        alias: 'access_way',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        filter: {
          field: 'access_ways.desc',
        },
      },
      {
        key: 'entityId',
        labelId: 'field.entity',
        field: 'entityId',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'entity_id',
        },
      },
      {
        key: 'beginLastAccess',
        labelId: 'field.afterLastAccess',
        field: 'beginLastAccess',
        alias: 'last_access',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'after',
        filter: {
          field: 'last_installation_log.date',
        },
      },
      {
        key: 'endLastAccess',
        labelId: 'field.beforeLastAccess',
        field: 'endLastAccess',
        alias: 'last_access',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'before',
        filter: {
          field: 'last_installation_log.date',
        },
      },
      {
        key: 'synchronized',
        labelId: 'field.synchronized',
        field: 'synchronized',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.boolean,
        filter: {
          field: 'sync_status',
        },
      },
      {
        key: 'opBookingId',
        labelId: 'field.opBookingId',
        field: 'opBookingId',
        alias: 'booking_id',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        scope: 'eq',
        type: SEARCHFIELD_TYPES.string,
        filter: {
          field: 'metadata.booking_id',
        },
      },
    ],
  }),
};

module.exports = customer;



// WEBPACK FOOTER //
// ./models/customer.js